import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  ElectronicSignatureBlock,
  ElectronicSignatureTitleLevel,
} from '@course-authoring/shared-types';
import { sanitizeRichTextContent, useTheme } from '@course-authoring/shared-types';
import { createContentBlockStyles, useIsMobileViewport } from '@course-authoring/ui-blocks';
import { InteractionRichTextField } from './InteractionRichTextField';
import { createTokenRichTextToolbarStyles } from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';

type FieldName = 'title' | 'description' | 'checkboxLabel';

export interface ElectronicSignatureEditorProps {
  block: ElectronicSignatureBlock;
  onChange: (updates: Partial<ElectronicSignatureBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function ElectronicSignatureEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: ElectronicSignatureEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const baseStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const titleLevel = block.titleLevel ?? 'h1';
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);
  const sanitizedDescription = useMemo(
    () =>
      typeof block.description === 'string'
        ? { type: 'doc' as const, content: [{ type: 'paragraph' as const }] }
        : sanitizeRichTextContent(block.description as JSONContent),
    [block.description]
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ title: e.target.value });
    },
    [onChange]
  );

  const handleCheckboxLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ checkboxLabel: e.target.value });
    },
    [onChange]
  );

  const styles: Record<string, CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      width: '100%',
      fontFamily: tokens.typography.fontFamily,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    titleInput: getTitleInputStyle(titleLevel, tokens),
    descriptionWrapper: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: focusedField === 'description'
        ? tokens.ui.selectionBorder
        : tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: tokens.spacing.md,
      transition: `border-color ${tokens.ui.transitionDefault}`,
    },
    checkboxLabelInput: {
      fontSize: '14px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: focusedField === 'checkboxLabel'
        ? tokens.ui.selectionBorder
        : tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      width: '100%',
      boxSizing: 'border-box' as const,
      outline: 'none',
      transition: `border-color ${tokens.ui.transitionDefault}`,
    },
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.section}>
        <label style={styles.label}>Title</label>
        {!translationMode ? <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => onChange({ titleLevel: level as ElectronicSignatureTitleLevel })}
          tokens={tokens}
        /> : null}
        <input
          type="text"
          value={block.title}
          onChange={handleTitleChange}
          onFocus={() => setFocusedField('title')}
          onBlur={() => setFocusedField(null)}
          placeholder="Enter title..."
          style={styles.titleInput}
          autoFocus={autoFocus}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Description</label>
        <div style={styles.descriptionWrapper}>
          <InteractionRichTextField
            content={sanitizedDescription}
            placeholder="Enter description (optional)..."
            isFocused={focusedField === 'description'}
            onFocus={() => setFocusedField('description')}
            onBlur={() => setFocusedField(null)}
            onContentChange={(content) => onChange({ description: content })}
            autoFocus={false}
            containerRef={containerRef}
            toolbarPreset="full"
            toolbarStyles={toolbarStyles}
            editorInlineStyle={`outline: none; min-height: ${isMobile ? '40px' : '60px'};`}
            shouldRerenderOnTransaction
          />
        </div>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Checkbox Label</label>
        <input
          type="text"
          value={block.checkboxLabel}
          onChange={handleCheckboxLabelChange}
          onFocus={() => setFocusedField('checkboxLabel')}
          onBlur={() => setFocusedField(null)}
          placeholder="Enter checkbox label..."
          style={styles.checkboxLabelInput}
        />
      </div>
    </div>
  );
}

ElectronicSignatureEditor.displayName = 'ElectronicSignatureEditor';
